import React from 'react';
import { useLocation } from 'react-router-dom';
import { envConfig } from 'constants/envConfig';
import { sentryRepository } from 'repositories/SentryRepository';
import { ErrorCode } from './enums/ErrorCode';
import { ErrorName } from './enums/ErrorName';
import { GeneralError } from './GeneralError';

interface IError extends Error {
    code: string;
}

interface IProps {
    FallBackComponent?: React.FC;
    pathname?: string;
}

interface IState {
    hasError: boolean;
    chunkError: boolean;
}

class ErrorBoundaryComponent extends React.Component<React.PropsWithChildren<IProps>, IState> {
    static getDerivedStateFromError(error: IError): IState {
        return {
            hasError: true,
            chunkError: error?.name === ErrorName.chunk || error?.code === ErrorCode.cssChunk,
        };
    }

    constructor(props: IProps) {
        super(props);

        this.state = {
            hasError: false,
            chunkError: false,
        };
    }

    componentDidUpdate(prevProps: Readonly<IProps>): void {
        if (prevProps.pathname !== this.props.pathname && this.state.hasError) {
            this.setState({ hasError: false, chunkError: false });
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        if (this.state.chunkError) {
            if (!envConfig.isLocalEnv) {
                window.location.reload();
            }
            return;
        }
        sentryRepository.error(error, { info: errorInfo });
        console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        if (this.state.chunkError) {
            return <GeneralError />;
        }

        if (this.state.hasError) {
            const ErrorComponent = this.props.FallBackComponent;

            if (ErrorComponent) {
                return <ErrorComponent />;
            }

            return <GeneralError />;
        }

        return this.props.children;
    }
}

export const ErrorBoundary: React.FC<Partial<React.PropsWithChildren<IProps>>> = (props) => {
    const location = useLocation();

    return (
        <ErrorBoundaryComponent
            pathname={location.pathname}
            FallBackComponent={props.FallBackComponent}
        >
            {props.children}
        </ErrorBoundaryComponent>
    );
};
